import { useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  AlertCircle,
  Download,
  Rocket,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppProviders";
import { SUPPORT_EMAIL } from "@/config";

const INSTALLER_URL = "/SecureMaintPro-Setup-1.0.0.exe";

const inputBase =
  "w-full rounded-sm border bg-secondary/40 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-colors duration-200";

const FieldError = ({ field, message }) =>
  message ? (
    <p
      data-testid={`trial-error-${field}`}
      className="mt-1.5 flex items-center gap-1.5 text-xs text-red-500"
    >
      <AlertCircle className="h-3.5 w-3.5" />
      {message}
    </p>
  ) : null;

const Trial = () => {
  const { t } = useApp();
  const tr = t.trial;

  const [form, setForm] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    hardwareId: "",
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const update = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const n = { ...prev };
      delete n[field];
      return n;
    });
  };

  const handleDownload = () => {
    if (INSTALLER_URL && INSTALLER_URL !== "#")
      window.open(INSTALLER_URL, "_blank", "noopener");
    toast.success(t.downloads.started, {
      description: "SecureMaintPro-Setup-1.0.6.exe",
      duration: 3500,
    });
  };

  const validate = () => {
    const next = {};
    if (!form.company.trim()) next.company = tr.errors.company;
    if (!form.name.trim()) next.name = tr.errors.name;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = tr.errors.email;
    if (!form.phone.trim()) next.phone = tr.errors.phone;
    if (!form.hardwareId.trim()) next.hardwareId = tr.errors.hardwareId;
    return next;
  };

  const buildMailto = () => {
    const L = tr.mail.labels;
    const body = [
      "SecureMaintPro — Aanvraag Gratis Testlicentie / Free Trial Request",
      "----------------------------------------",
      `${L.company}: ${form.company}`,
      `${L.name}: ${form.name}`,
      `${L.email}: ${form.email}`,
      `${L.phone}: ${form.phone}`,
      `${L.hardwareId}: ${form.hardwareId}`,
    ].join("\n");
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      tr.mail.subject
    )}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    window.location.href = buildMailto();
    setSubmitted(true);
  };

  const close = () => {
    setSubmitted(false);
    setForm({ company: "", name: "", email: "", phone: "", hardwareId: "" });
  };

  return (
    <section
      id="trial"
      data-testid="trial-section"
      className="py-24 sm:py-32 border-t border-border bg-card/30"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-sm border border-blue-600/40 bg-blue-600/10 px-3 py-1.5 font-mono text-xs font-medium text-blue-500">
            <Rocket className="h-3.5 w-3.5" />
            {tr.badge}
          </div>
          <p className="mt-4 font-mono text-sm uppercase tracking-widest text-blue-500">
            {tr.label}
          </p>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {tr.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {tr.subtitle}
          </p>
        </motion.div>

        <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:items-start">
          {/* Steps */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="font-heading text-lg font-medium tracking-tight">
              {tr.stepsTitle}
            </h3>
            <div className="mt-6 space-y-6">
              {tr.steps.map((step, i) => (
                <div
                  key={step.title}
                  data-testid={`trial-step-${i + 1}`}
                  className="flex gap-4"
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-sm border border-blue-600 font-mono text-sm font-bold text-blue-500">
                    {i + 1}
                  </span>
                  <div>
                    <h4 className="font-medium">{step.title}</h4>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {step.desc}
                    </p>
                    {i === 0 && (
                      <button
                        onClick={handleDownload}
                        data-testid="trial-download"
                        className="mt-3 inline-flex h-10 items-center gap-2 rounded-sm bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-200"
                      >
                        <Download className="h-4 w-4" />
                        {tr.downloadBtn}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-sm border border-border bg-card p-6 sm:p-8"
          >
            <h3 className="font-heading text-lg font-medium tracking-tight">
              {tr.form.title}
            </h3>
            <form onSubmit={handleSubmit} noValidate data-testid="trial-form" className="mt-5">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    {tr.form.company}
                  </label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    placeholder={tr.form.companyPh}
                    data-testid="trial-input-company"
                    className={`${inputBase} ${errors.company ? "border-red-500" : "border-border"}`}
                  />
                  <FieldError field="company" message={errors.company} />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    {tr.form.name}
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder={tr.form.namePh}
                    data-testid="trial-input-name"
                    className={`${inputBase} ${errors.name ? "border-red-500" : "border-border"}`}
                  />
                  <FieldError field="name" message={errors.name} />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      {tr.form.email}
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder={tr.form.emailPh}
                      data-testid="trial-input-email"
                      className={`${inputBase} ${errors.email ? "border-red-500" : "border-border"}`}
                    />
                    <FieldError field="email" message={errors.email} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      {tr.form.phone}
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder={tr.form.phonePh}
                      data-testid="trial-input-phone"
                      className={`${inputBase} ${errors.phone ? "border-red-500" : "border-border"}`}
                    />
                    <FieldError field="phone" message={errors.phone} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1.5">
                    {tr.form.hardwareId}
                  </label>
                  <input
                    type="text"
                    value={form.hardwareId}
                    onChange={update("hardwareId")}
                    placeholder={tr.form.hardwareIdPh}
                    data-testid="trial-input-hardware-id"
                    className={`${inputBase} font-mono ${errors.hardwareId ? "border-red-500" : "border-border"}`}
                  />
                  <FieldError field="hardwareId" message={errors.hardwareId} />
                </div>
              </div>

              <button
                type="submit"
                data-testid="trial-submit"
                className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-sm bg-blue-600 px-6 text-sm font-medium text-white hover:bg-blue-500 transition-colors duration-200"
              >
                <Rocket className="h-4 w-4" />
                {tr.form.submit}
              </button>

              <p className="mt-4 text-xs leading-relaxed text-muted-foreground/70">
                {tr.form.noteBefore}{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="text-blue-500 underline-offset-2 hover:underline"
                >
                  {SUPPORT_EMAIL}
                </a>
                .
              </p>
            </form>
          </motion.div>
        </div>
      </div>

      {/* Success modal */}
      {submitted && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-6"
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="trial-success"
            className="relative w-full max-w-md rounded-sm border border-border bg-card p-8 text-center"
          >
            <button
              onClick={close}
              data-testid="trial-success-close"
              aria-label="Close"
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-sm bg-blue-600/10 text-blue-500">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h3 className="font-heading mt-5 text-xl font-semibold tracking-tight">
              {tr.success.title}
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              {tr.success.body}
            </p>
            <button
              onClick={close}
              className="mt-6 inline-flex h-10 items-center justify-center rounded-sm border border-border bg-secondary/50 px-5 text-sm font-medium hover:bg-secondary transition-colors duration-200"
            >
              {tr.success.close}
            </button>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default Trial;
